import { Component, h, useEffect, useRef, useState } from "@lukekaalim/act";
import { RoomID } from "../../defs";
import { Channel, HttpApi, NetworkClient } from "@kosmod/pancake";
import { conciergeApiDef } from "../../defs/api";
import { useFrontendCore } from "@kosmod/core";
import { Theatre, theatreAPI, TheatreClient, useTheatreCoreResource } from "@kosmod/theatre";
import { AuthState, GameID, GamesClient } from "@kosmod/sesame";
import { useAsync, useLogger } from "@kosmod/kit";
import { createRoomSessionClient, RoomSessionChannel } from "../../backend";
import { useRemoteTheatreController } from "@kosmod/theatre";
import { useConciergeState } from "../../hooks/useConciergeState";
import { html } from "@lukekaalim/act-web";
import { ConciergeOverlay } from "../ConciergeOverlay";
import { AssetClient, AssetConfig, assetsAPI, AssetURLMap, GameAssetClient, useGameAssetClientProvider } from "@kosmod/assets";
import { useTheatreSceneResources } from "@kosmod/theatre";
import { Loading } from "../Loading";

export type RoomPageProps = {
  roomId: RoomID,
  gameId: GameID,

  urls: {
    assets: AssetURLMap,
    session: HttpApi.URLMap<typeof conciergeApiDef>,
  },

  config: {
    asset: AssetConfig,
    apiBaseURL: URL,
  },

  clients: {
    net: NetworkClient,
    theatre: TheatreClient,
    assets: AssetClient,
    auth: AuthState,
    games: GamesClient,
    concierge: HttpApi.Handler<'client', typeof conciergeApiDef>,
  }
}

const Background: Component = ({ children }) => {
  return h(html.div, { style: {
    background: 'black', overflow: 'hidden', height: '100%', width: '100%', display: 'flex', flexDirection: 'column',
    position: 'relative'
  } }, children)
}

export const RoomPage: Component<RoomPageProps> = ({ roomId, gameId, clients, config, urls }) => {
  const assets = useGameAssetClientProvider(gameId, config.asset.strategy, clients.net, urls.assets)

  if (clients.auth.authority !== 'user' && clients.auth.authority !== 'guest')
    return 'Not Logged in';

  const [res, coreLoadables] = useTheatreCoreResource();
  const { result: roomContext } = useAsync(async () => clients.concierge["room/context"].GET({ room: roomId, game: gameId }, null), [
    gameId, roomId, clients.concierge
  ])

  if (!res || !roomContext || !clients.auth.account)
    return h(Background, {}, [
      h(Loading, {
        ready: false,
        connection: {
          target: urls.session["room/join"].href,
          connected: 'not-started',
        },
        sections: [{
          name: 'Core',
          loadables: coreLoadables
        }]
      })
    ])

  if (roomContext.op == 'error')
    return 'UHOH'

  const { game, self, room } = roomContext;

  const logger = useLogger(`RoomPage(${room.id})`);

  const [port, setPort] = useState<Channel.Port.Client<RoomSessionChannel> | null>(null);

  
  useEffect(() => {
    logger.log('Making Connection to', game.id)

    const connection = clients.concierge['room/join'].connect({ game: game.id, room: room.id });
    setPort(connection.port);

    connection.disconnected.then(() => setPort(null))

    return () => {
      connection.disconnect();
    }
  }, [clients.concierge, game]);

  if (!port)
    return h(Background, {}, [
      h(Loading, {
        ready: false,
        connection: {
          target: urls.session["room/join"].href,
          connected: 'initiated',
        },
        sections: [{
          name: 'Core',
          loadables: coreLoadables
        }]
      })
    ])

  const roomClient = useRef(() => createRoomSessionClient(self, port, logger.createSubsystem(`RoomHostClient`))).get();
  
  const concierge = useConciergeState(gameId, clients.auth, clients.auth.account, roomClient, self, clients);
  const [controller, data, dataLoadables] = useRemoteTheatreController(game, roomClient.scene, self,  concierge.players, { ...clients, assets });
  const [sceneRes, sceneLoadables] = useTheatreSceneResources(data, assets, res)

  const [accept, setAccept] = useState(false);

  if (!data.ready || !sceneRes || !accept)
    return h(Background, {}, [
      h(Loading, {
        onAcceptJoinClick() {
          setAccept(true)
        },

        otherPlayers: Math.max(0, concierge.players.length - 1),
        roomName: room.name,
        connection: {
          target: urls.session["room/join"].href,
          connected: 'connected',
        },
        sections: [
          {
            name: 'Core',
            loadables: coreLoadables
          },
          {
            name: 'Game Data',
            loadables: dataLoadables
          },
          {
            name: 'Room Graphics',
            loadables: sceneLoadables
          },
        ]
      })
    ])

  

  return h(Background, {}, [
    h(Loading, {
      visible: false,
      ready: false,
      otherPlayers: concierge.players.length,
      connection: {
        target: urls.session["room/join"].href,
        connected: 'connected',
      },
      sections: [
        {
          name: 'Core',
          loadables: coreLoadables
        },
        {
          name: 'Game Data',
          loadables: dataLoadables
        },
        {
          name: 'Room Graphics',
          loadables: sceneLoadables
        },
      ]
    }),
    h(Theatre, { res, controller, data, concierge, clients: {
      ...clients,
      assets,
    }, sceneRes }),
    h(ConciergeOverlay, { state: concierge, port, rtcClient: clients.net.rtc })
  ]);
};